"use client";

import { useCallback, useMemo, useReducer } from "react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import {
  activeTicketPrice,
  BUCKET_FILTERS,
  DEFAULT_SORT,
  matchesBucket,
  NUMERIC_FILTERS,
  RANGE_FILTERS,
  SUBCATEGORY_TO_CATEGORY,
  type BucketKey,
  type PrizeCategory,
  type RangeKey,
  type SortKey,
} from "../lifestyle-constants";

export interface RangeValue {
  min: number;
  max: number;
}

export interface RangeBounds extends RangeValue {
  step: number;
  /** False when no prize in the current data carries this measurement. */
  applicable: boolean;
}

/** One checkbox option — a make, a style, or a band within a bucket filter. */
export interface Facet {
  /** The value committed to state and the URL. */
  value: string;
  /** Display text, when it differs from the value. */
  label?: string;
  count: number;
  disabled: boolean;
  selected: boolean;
}

export interface LifestyleFilterState {
  keyword: string;
  category: PrizeCategory | null;
  makes: string[];
  styles: string[];
  sort: SortKey;
  /** Only holds ranges the user has actually moved off their bounds. */
  ranges: Partial<Record<RangeKey, RangeValue>>;
  /** Selected band ids per bucket filter. An empty list means "any". */
  buckets: Partial<Record<BucketKey, string[]>>;
}

export const INITIAL_FILTER_STATE: LifestyleFilterState = {
  keyword: "",
  category: null,
  makes: [],
  styles: [],
  sort: DEFAULT_SORT,
  ranges: {},
  buckets: {},
};

export type FilterGroup = "keyword" | "category" | "makes" | "styles" | "sort" | "ranges" | "buckets";

type FilterAction =
  | { type: "setKeyword"; keyword: string }
  | { type: "setCategory"; category: PrizeCategory | null }
  | { type: "setMakes"; makes: string[] }
  | { type: "toggleMake"; make: string }
  | { type: "setStyles"; styles: string[] }
  | { type: "toggleStyle"; style: string }
  | { type: "setSort"; sort: SortKey }
  | { type: "setRange"; key: RangeKey; value: RangeValue | null }
  | { type: "toggleBucket"; key: BucketKey; bucketId: string }
  | { type: "clearBucket"; key: BucketKey }
  | { type: "clear"; group?: FilterGroup }
  | { type: "hydrate"; state: Partial<LifestyleFilterState> };

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter(item => item !== value) : [...list, value];
}

function filterReducer(state: LifestyleFilterState, action: FilterAction): LifestyleFilterState {
  switch (action.type) {
    case "setKeyword":
      return { ...state, keyword: action.keyword };
    case "setCategory": {
      // Makes and styles are category-specific, so they cannot survive the
      // switch. Neither can a measurement the new category does not offer: the
      // drawer would have no row for it, leaving an invisible filter that
      // quietly excludes everything.
      const ranges = { ...state.ranges };
      const buckets = { ...state.buckets };

      for (const filter of NUMERIC_FILTERS) {
        const applies =
          action.category === null || filter.categories.length === 0 || filter.categories.includes(action.category);
        if (applies) continue;

        if (filter.kind === "range") delete ranges[filter.key];
        else delete buckets[filter.key];
      }

      return { ...state, category: action.category, makes: [], styles: [], ranges, buckets };
    }
    case "setMakes":
      return { ...state, makes: action.makes };
    case "toggleMake":
      return { ...state, makes: toggle(state.makes, action.make) };
    case "setStyles":
      return { ...state, styles: action.styles };
    case "toggleStyle":
      return { ...state, styles: toggle(state.styles, action.style) };
    case "setSort":
      return { ...state, sort: action.sort };
    case "setRange": {
      const ranges = { ...state.ranges };
      if (action.value === null) {
        delete ranges[action.key];
      } else {
        ranges[action.key] = action.value;
      }
      return { ...state, ranges };
    }
    case "toggleBucket": {
      const next = toggle(state.buckets[action.key] ?? [], action.bucketId);
      const buckets = { ...state.buckets };
      if (next.length === 0) {
        delete buckets[action.key];
      } else {
        buckets[action.key] = next;
      }
      return { ...state, buckets };
    }
    case "clearBucket": {
      const buckets = { ...state.buckets };
      delete buckets[action.key];
      return { ...state, buckets };
    }
    case "clear": {
      if (!action.group) return INITIAL_FILTER_STATE;
      switch (action.group) {
        case "keyword":
          return { ...state, keyword: "" };
        case "category":
          return { ...state, category: null, makes: [], styles: [] };
        case "makes":
          return { ...state, makes: [] };
        case "styles":
          return { ...state, styles: [] };
        case "sort":
          return { ...state, sort: DEFAULT_SORT };
        case "ranges":
          return { ...state, ranges: {} };
        case "buckets":
          return { ...state, buckets: {} };
        default:
          return state;
      }
    }
    case "hydrate":
      return { ...state, ...action.state };
    default:
      return state;
  }
}

export function categoryOf(prize: LifestylePrize): string {
  return SUBCATEGORY_TO_CATEGORY[prize.Subcategory] ?? "other";
}

/**
 * Keyword match.
 *
 * Ported from `prizeFilter` in botb.dailycontroller.js: a single term matches
 * make, model or any style; two terms are treated as "make model". Unlike the
 * original, a keyword narrows alongside the other filters rather than replacing
 * them — the Angular version returned early on a keyword and silently ignored
 * everything else, which the 2026 design's persistent filter bar and live
 * results count make untenable.
 */
function matchesKeyword(prize: LifestylePrize, keyword: string): boolean {
  const term = keyword.trim().toLowerCase();
  if (!term) return true;

  const make = (prize.Make ?? "").toLowerCase();
  const model = (prize.Model ?? "").toLowerCase();
  const styles = (prize.Styles ?? []).map(style => (style ?? "").toLowerCase());

  // The whole term against the full title first. Picking "Range Rover Evoque
  // Autobiography" from the suggestions would otherwise match nothing: the
  // two-term rule reads "Range" as the make and "Rover" as the model.
  if (`${make} ${model}`.trim().includes(term)) return true;

  const [first, second] = term.split(/\s+/);
  if (second) {
    if (make.includes(first) && model.includes(second)) return true;
  } else if (make.includes(term) || model.includes(term)) {
    return true;
  }

  return styles.some(style => style.includes(first));
}

function sortPrizes(prizes: LifestylePrize[], sort: SortKey): LifestylePrize[] {
  const byName = (a: LifestylePrize, b: LifestylePrize) =>
    (a.Make ?? "").localeCompare(b.Make ?? "") || (a.Model ?? "").localeCompare(b.Model ?? "");

  // Angular treated a null SortGroup as 10000 so ungrouped prizes sink last.
  const sortGroup = (prize: LifestylePrize) => prize.SortGroup ?? 10000;

  const sorted = [...prizes];
  switch (sort) {
    case "recommended":
      return sorted.sort(
        (a, b) => sortGroup(a) - sortGroup(b) || activeTicketPrice(b) - activeTicketPrice(a) || byName(a, b)
      );
    case "price-high":
      return sorted.sort((a, b) => activeTicketPrice(b) - activeTicketPrice(a) || byName(a, b));
    case "price-low":
      return sorted.sort((a, b) => activeTicketPrice(a) - activeTicketPrice(b) || byName(a, b));
    case "a-z":
      return sorted.sort(byName);
    case "z-a":
      return sorted.sort((a, b) => byName(b, a));
    default:
      return sorted;
  }
}

/**
 * Owns every filter on the page and the prize list they produce.
 *
 * Replaces `prizeFilter`, `sortResult`, `getMinValue`/`getMaxValue`,
 * `updateFilterValues`, `shouldDisableMake`/`shouldDisableStyle`,
 * `checkIfFilterIsDirty` and `clearAllFilters`.
 *
 * @param prizes All prizes loaded for the page, already deduped.
 * @param matchesSelectedDate Date predicate from `useDailyCompetitions`.
 */
export default function useLifestyleFilters(
  prizes: LifestylePrize[],
  matchesSelectedDate: (prize: LifestylePrize) => boolean = () => true
) {
  const [state, dispatch] = useReducer(filterReducer, INITIAL_FILTER_STATE);

  /** Prizes still on the page once the date filter has been applied. */
  const datedPrizes = useMemo(() => prizes.filter(matchesSelectedDate), [prizes, matchesSelectedDate]);

  /**
   * Prizes in the selected category, before any other filter is applied.
   *
   * Bounds and option lists derive from this rather than the whole page, so the
   * Bikes RRP track spans bike prices instead of stretching to a £132k car, and
   * the Cars make list does not offer watch brands.
   */
  const scopedPrizes = useMemo(
    () => (state.category ? datedPrizes.filter(prize => categoryOf(prize) === state.category) : datedPrizes),
    [datedPrizes, state.category]
  );

  /**
   * Slider bounds, derived from the data rather than the hardcoded floors in
   * the Angular scope. Prizes with no value for a measurement are excluded, so
   * a cash prize's implicit 0cc engine does not drag the floor to zero.
   */
  const bounds = useMemo(() => {
    const result = {} as Record<RangeKey, RangeBounds>;

    for (const { key, valueOf, step } of RANGE_FILTERS) {
      const values = scopedPrizes.map(valueOf).filter(value => Number.isFinite(value) && value > 0);

      if (values.length === 0) {
        result[key] = { min: 0, max: 0, step, applicable: false };
        continue;
      }

      const min = Math.min(...values);
      const max = Math.max(...values);
      // A single distinct value gives the slider nothing to do.
      result[key] = { min, max, step, applicable: max > min };
    }

    return result;
  }, [scopedPrizes]);

  /**
   * Whether any prize carries a given measurement at all. Decides which bucket
   * filters the drawer offers, mirroring `applicable` on the slider bounds.
   */
  const bucketApplicable = useMemo(() => {
    const result = {} as Record<BucketKey, boolean>;
    for (const { key, valueOf } of BUCKET_FILTERS) {
      result[key] = scopedPrizes.some(prize => {
        const value = valueOf(prize);
        return Number.isFinite(value) && value > 0;
      });
    }
    return result;
  }, [scopedPrizes]);

  const predicates = useMemo(() => {
    const rangeEntries = Object.entries(state.ranges) as Array<[RangeKey, RangeValue]>;

    /** Bands within one filter are an OR; separate filters are an AND. */
    const matchesBucketFilter = (prize: LifestylePrize, key: BucketKey, ids: string[]) => {
      if (ids.length === 0) return true;
      const definition = BUCKET_FILTERS.find(filter => filter.key === key);
      if (!definition) return true;
      const value = definition.valueOf(prize);
      return definition.buckets.some(bucket => ids.includes(bucket.id) && matchesBucket(value, bucket));
    };

    return {
      keyword: (prize: LifestylePrize) => matchesKeyword(prize, state.keyword),
      category: (prize: LifestylePrize) => !state.category || categoryOf(prize) === state.category,
      makes: (prize: LifestylePrize) => state.makes.length === 0 || state.makes.includes(prize.Make),
      styles: (prize: LifestylePrize) =>
        state.styles.length === 0 || (prize.Styles ?? []).some(style => state.styles.includes(style)),
      ranges: (prize: LifestylePrize) =>
        rangeEntries.every(([key, { min, max }]) => {
          const definition = RANGE_FILTERS.find(range => range.key === key);
          if (!definition) return true;
          const value = definition.valueOf(prize);
          return value >= min && value <= max;
        }),
      buckets: (prize: LifestylePrize) =>
        (Object.entries(state.buckets) as Array<[BucketKey, string[]]>).every(([key, ids]) =>
          matchesBucketFilter(prize, key, ids)
        ),
    };
  }, [state.keyword, state.category, state.makes, state.styles, state.ranges, state.buckets]);

  /**
   * Applies every predicate except the named one. Facet counts need this:
   * "Audi (4)" should mean "4 results if you also tick Audi", so the make facet
   * must not be narrowed by the make selection itself.
   */
  const applyExcept = useCallback(
    (except?: keyof typeof predicates) =>
      datedPrizes.filter(prize =>
        (Object.keys(predicates) as Array<keyof typeof predicates>)
          .filter(name => name !== except)
          .every(name => predicates[name](prize))
      ),
    [datedPrizes, predicates]
  );

  const filteredPrizes = useMemo(() => sortPrizes(applyExcept(), state.sort), [applyExcept, state.sort]);

  const makeFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const prize of applyExcept("makes")) {
      if (!prize.Make) continue;
      counts.set(prize.Make, (counts.get(prize.Make) ?? 0) + 1);
    }

    const universe = new Set(scopedPrizes.map(prize => prize.Make).filter(Boolean));
    return Array.from(universe)
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({
        value,
        count: counts.get(value) ?? 0,
        disabled: (counts.get(value) ?? 0) === 0,
        selected: state.makes.includes(value),
      }));
  }, [applyExcept, scopedPrizes, state.makes]);

  const styleFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const prize of applyExcept("styles")) {
      for (const style of prize.Styles ?? []) {
        if (!style) continue;
        counts.set(style, (counts.get(style) ?? 0) + 1);
      }
    }

    const universe = new Set(scopedPrizes.flatMap(prize => prize.Styles ?? []).filter(Boolean));
    return Array.from(universe)
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({
        value,
        count: counts.get(value) ?? 0,
        disabled: (counts.get(value) ?? 0) === 0,
        selected: state.styles.includes(value),
      }));
  }, [applyExcept, scopedPrizes, state.styles]);

  /**
   * Band counts per bucket filter, read as "results if you also tick this".
   *
   * A filter's own bands must not narrow its own counts, but the other bucket
   * filters still do — so ticking "Under £1" is reflected in the Top Speed
   * counts, while the Ticket Price counts stay comparable to each other.
   */
  const bucketFacets = useMemo(() => {
    const pool = applyExcept("buckets");
    const result = {} as Record<BucketKey, Facet[]>;

    for (const definition of BUCKET_FILTERS) {
      const narrowed = pool.filter(prize =>
        BUCKET_FILTERS.every(other => {
          if (other.key === definition.key) return true;
          const ids = state.buckets[other.key] ?? [];
          if (ids.length === 0) return true;
          const value = other.valueOf(prize);
          return other.buckets.some(bucket => ids.includes(bucket.id) && matchesBucket(value, bucket));
        })
      );

      const selected = state.buckets[definition.key] ?? [];
      result[definition.key] = definition.buckets.map(bucket => {
        const count = narrowed.filter(prize => matchesBucket(definition.valueOf(prize), bucket)).length;
        return {
          value: bucket.id,
          label: bucket.label,
          count,
          disabled: count === 0 && !selected.includes(bucket.id),
          selected: selected.includes(bucket.id),
        };
      });
    }

    return result;
  }, [applyExcept, state.buckets]);

  const categoryFacets = useMemo(() => {
    const pool = applyExcept("category");
    const counts = new Map<string, number>();
    for (const prize of pool) {
      const category = categoryOf(prize);
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }
    return counts;
  }, [applyExcept]);

  const isDirty = useMemo(
    () =>
      state.keyword !== "" ||
      state.category !== null ||
      state.makes.length > 0 ||
      state.styles.length > 0 ||
      state.sort !== DEFAULT_SORT ||
      Object.keys(state.ranges).length > 0 ||
      Object.keys(state.buckets).length > 0,
    [state]
  );

  /** Badge count for the "All filters" button; sort is not a filter. */
  const activeFilterCount = useMemo(
    () =>
      (state.keyword ? 1 : 0) +
      (state.category ? 1 : 0) +
      state.makes.length +
      state.styles.length +
      Object.keys(state.ranges).length +
      Object.values(state.buckets).reduce((total, ids) => total + ids.length, 0),
    [state]
  );

  const actions = useMemo(
    () => ({
      setKeyword: (keyword: string) => dispatch({ type: "setKeyword", keyword }),
      setCategory: (category: PrizeCategory | null) => dispatch({ type: "setCategory", category }),
      setMakes: (makes: string[]) => dispatch({ type: "setMakes", makes }),
      toggleMake: (make: string) => dispatch({ type: "toggleMake", make }),
      setStyles: (styles: string[]) => dispatch({ type: "setStyles", styles }),
      toggleStyle: (style: string) => dispatch({ type: "toggleStyle", style }),
      setSort: (sort: SortKey) => dispatch({ type: "setSort", sort }),
      setRange: (key: RangeKey, value: RangeValue | null) => dispatch({ type: "setRange", key, value }),
      toggleBucket: (key: BucketKey, bucketId: string) => dispatch({ type: "toggleBucket", key, bucketId }),
      clearBucket: (key: BucketKey) => dispatch({ type: "clearBucket", key }),
      clear: (group?: FilterGroup) => dispatch({ type: "clear", group }),
      hydrate: (next: Partial<LifestyleFilterState>) => dispatch({ type: "hydrate", state: next }),
    }),
    []
  );

  return {
    filters: state,
    ...actions,
    bounds,
    bucketApplicable,
    /** What the search dropdown draws its suggestions from. */
    suggestionPool: scopedPrizes,
    filteredPrizes,
    resultCount: filteredPrizes.length,
    makeFacets,
    styleFacets,
    bucketFacets,
    categoryFacets,
    isDirty,
    activeFilterCount,
  };
}
